import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FormEvent } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Delete, Edit, PermMedia } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import LoadingSpinner from "../components/LoadingSpinner";
import { useAuth } from "../hooks/useAuth";
import { useRolePermissions } from "../hooks/useRolePermissions";
import {
  adminEventAPI,
  cameraAPI,
  childAPI,
  classroomAPI,
  teacherAPI,
} from "../utils/api";
import type {
  AdminEvent,
  AdminEventRequest,
  EventOption,
  EventOptions,
} from "../types/adminEvent";
import {
  captureFields,
  changeClassroom,
  classroomOptions,
  emptyEvent,
  eventError,
  eventTypes,
  localDate,
  localTime,
  mediaAccept,
  nurseryOptions,
  validateEvent,
  validateMedia,
} from "../utils/adminEvents";
import type { TeacherEventType } from "../types/teacher";

const typeColors: Record<TeacherEventType, string> = {
  birthday: "#F8DE8A",
  nursery_event: "#A7E5DE",
  graduation: "#BDE7D7",
  holiday: "#F5C56E",
  field_trip: "#F6B6BB",
  special_event: "#D8C7F7",
};

// A nursery-keyed subtree owns every form, dialog and request. A route/context
// mismatch renders no event state, including during the selector's navigation.
export default function AdminEvents() {
  const { nursery_id } = useParams();
  const { currentNursery, nurseriesLoading, userRolePermissionsLoading } =
    useAuth();
  const permissions = useRolePermissions();
  const { t } = useTranslation("pages", { keyPrefix: "teacherEvents" });
  return (
    <Layout title={t("title")}>
      {nurseriesLoading ||
      userRolePermissionsLoading ||
      currentNursery?.nursery_id !== nursery_id ? (
        <LoadingSpinner />
      ) : !nursery_id ? (
        <Alert severity="info">{t("selectNursery")}</Alert>
      ) : !permissions.canViewEvents ? (
        <Alert severity="error">
          {t("accessDenied")}
        </Alert>
      ) : (
        <NurseryEvents
          key={nursery_id}
          nurseryId={nursery_id}
          canCreate={permissions.canCreateEvents}
          canEdit={permissions.canEditEvents}
          canDelete={permissions.canDeleteEvents}
        />
      )}
    </Layout>
  );
}

function NurseryEvents({
  nurseryId,
  canCreate,
  canEdit,
  canDelete,
}: {
  nurseryId: string;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
}) {
  const { t } = useTranslation("pages", { keyPrefix: "teacherEvents" });
  const [events, setEvents] = useState<AdminEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState<AdminEventRequest>(emptyEvent);
  const [editing, setEditing] = useState<AdminEvent | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [detail, setDetail] = useState<AdminEvent | null>(null);
  const [deleting, setDeleting] = useState<AdminEvent | null>(null);
  const [removingMedia, setRemovingMedia] = useState<number | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<Record<string, string>>(
    {},
  );
  const [fileInputKey, setFileInputKey] = useState(0);
  const [options, setOptions] = useState<EventOptions>({
    teachers: [],
    classrooms: [],
    children: [],
    cameras: [],
  });
  const [optionsReady, setOptionsReady] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const scope = useRef<AbortController | null>(null);
  const listRequest = useRef(0);
  const detailRef = useRef(detail);
  detailRef.current = detail;
  const active = () => scope.current !== null && !scope.current.signal.aborted;
  const filteredEvents = useMemo(
    () =>
      events.filter((e) => {
        if (typeFilter && e.type !== typeFilter) return false;
        const d = localDate(e.date);
        if (dateFrom && d < dateFrom) return false;
        if (dateTo && d > dateTo) return false;
        return true;
      }),
    [events, typeFilter, dateFrom, dateTo],
  );
  const checkEvent = useCallback(
    (event: AdminEvent) => {
      if (String(event.nursery_id) !== nurseryId)
        throw new Error(
          t("wrongNursery"),
        );
      return event;
    },
    [nurseryId, t],
  );
  const loadEvents = useCallback(
    async (quiet = false) => {
      const signal = scope.current?.signal;
      if (!signal) return;
      const request = ++listRequest.current;
      if (!quiet) {
        setLoading(true);
        setError("");
      }
      try {
        const result = await adminEventAPI.list(nurseryId, signal);
        if (!signal.aborted && request === listRequest.current)
          setEvents(result.events.map(checkEvent));
      } catch (err) {
        if (!signal.aborted && request === listRequest.current) {
          if (quiet) console.warn("Event polling failed", err);
          else setError(eventError(err));
        }
      } finally {
        if (!signal.aborted && request === listRequest.current)
          setLoading(false);
      }
    },
    [nurseryId, checkEvent],
  );
  useEffect(() => {
    const controller = new AbortController();
    scope.current = controller;
    void loadEvents();
    return () => controller.abort();
  }, [loadEvents]);
  useEffect(() => {
    if (!detail || detail.auto_attach_status !== "in_progress") return;
    const timer = window.setInterval(() => {
      const current = detailRef.current;
      if (current) void run((signal) => refreshDetail(current, signal));
    }, 30000);
    return () => window.clearInterval(timer);
  }, [detail?.id, detail?.auto_attach_status]);

  const loadOptions = async () => {
    const signal = scope.current!.signal;
    setOptionsLoading(true);
    setOptionsReady(false);
    try {
      const [teachers, classrooms, children, cameras] = await Promise.all([
        teacherAPI.list(nurseryId),
        classroomAPI.getClassrooms(nurseryId),
        childAPI.getChildren(nurseryId, true),
        cameraAPI.getCameras(nurseryId),
      ]);
      if (signal.aborted) return;
      setOptions({
        teachers: nurseryOptions(
          teachers.teachers || teachers.data || [],
          nurseryId,
        ),
        classrooms: nurseryOptions(classrooms.classrooms || [], nurseryId),
        children: nurseryOptions(children.children || [], nurseryId),
        cameras: nurseryOptions(cameras.cameras || [], nurseryId),
      });
      setOptionsReady(true);
    } catch (err) {
      if (!signal.aborted) setError(eventError(err));
    } finally {
      if (!signal.aborted) setOptionsLoading(false);
    }
  };
  const run = async (
    work: (signal: AbortSignal) => Promise<void>,
  ) => {
    if (busy || !active()) return;
    const signal = scope.current!.signal;
    setBusy(true);
    setError("");
    try {
      await work(signal);
    } catch (err) {
      if (!signal.aborted)
        setError(eventError(err));
    } finally {
      if (!signal.aborted) setBusy(false);
    }
  };
  const refreshDetail = async (event: AdminEvent, signal: AbortSignal) => {
    const result = await adminEventAPI.detail(
      nurseryId,
      checkEvent(event).id,
      signal,
    );
    if (!signal.aborted) setDetail(checkEvent(result.event));
  };
  const openForm = (event?: AdminEvent) => {
    if (event ? !canEdit : !canCreate) return;
    setError("");
    setSubmitAttempted(false);
    setEditing(event || null);
    setForm(
      event
        ? {
            title: event.title,
            type: event.type,
            date: localDate(event.date),
            teacher_id: event.teacher_id,
            classroom_id: event.classroom_id,
            child_id: event.child_id ?? null,
            camera_id: event.camera_id ?? null,
            start_time: localTime(event.start_time),
            end_time: localTime(event.end_time),
          }
        : emptyEvent(),
    );
    setFormOpen(true);
    void loadOptions();
  };
  const captureChanged = useMemo(
    () =>
      editing &&
      captureFields.some((key) => {
        const before =
          key === "date"
            ? localDate(editing.date)
            : key.endsWith("_time")
              ? localTime(editing[key] as string | null)
              : editing[key];
        return (form[key] ?? null) !== (before ?? null);
      }),
    [editing, form],
  );
  const validationError = useMemo(
    () => (optionsReady ? validateEvent(form, options, !!editing, t) : null),
    [form, options, optionsReady, editing, t],
  );
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!optionsReady || (editing ? !canEdit : !canCreate)) return;
    setSubmitAttempted(true);
    if (validationError) return;
    if (captureChanged && editing) {
      const hasCaptureHistory =
        editing.media.some((m) => m.source === "auto") ||
        ["in_progress", "completed", "failed"].includes(
          editing.auto_attach_status || "",
        );
      if (
        hasCaptureHistory &&
        !window.confirm(
          t("scheduleChangeConfirm"),
        )
      )
        return;
    }
    void run(async (signal) => {
      const payload = { ...form };
      if (editing)
        await adminEventAPI.update(
          nurseryId,
          checkEvent(editing).id,
          payload,
          signal,
        );
      else await adminEventAPI.create(nurseryId, payload, signal);
      if (signal.aborted) return;
      setFormOpen(false);
      setDetail(null);
      await loadEvents();
    });
  };
  const target = (event: AdminEvent) =>
    [
      event.classroom?.name ||
        (event.classroom_id ? t("classroomFallback", { id: event.classroom_id }) : ""),
      event.child?.name || (event.child_id ? t("childFallback", { id: event.child_id }) : ""),
    ]
      .filter(Boolean)
      .join(" / ") || t("wholeNursery");
  const owner = (event: AdminEvent) =>
    event.teacher_id == null
      ? t("adminManaged")
      : event.teacher?.name || t("teacherFallback", { id: event.teacher_id });
  const time = (event: AdminEvent) =>
    event.start_time
      ? `${localTime(event.start_time)}${event.end_time ? ` – ${localTime(event.end_time)}` : ""}`
      : t("noTimeSet");
  const status = (event: AdminEvent) => {
    if (!event.auto_attach_status) return null;
    const colors: Record<string, "default" | "info" | "success" | "error"> = {
      pending: "info",
      in_progress: "success",
      completed: "success",
      skipped: "default",
      failed: "error",
    };
    const chip = (
      <Chip
        size="small"
        color={colors[event.auto_attach_status] || "default"}
        label={t(`statuses.${event.auto_attach_status}`)}
      />
    );
    return event.auto_attach_status === "failed" && event.auto_attach_error ? (
      <Tooltip title={event.auto_attach_error}>{chip}</Tooltip>
    ) : (
      chip
    );
  };
  const select = (
    key: "teacher_id" | "classroom_id" | "child_id" | "camera_id",
    label: string,
    records: EventOption[],
    emptyLabel: string,
  ) => (
    <TextField
      select
      fullWidth
      label={label}
      value={form[key] ?? ""}
      disabled={busy || optionsLoading || !optionsReady}
      required={key === "teacher_id"}
      onChange={(event) => {
        const id = event.target.value ? Number(event.target.value) : null;
        setForm((current) =>
          key === "classroom_id"
            ? changeClassroom(current, id, options)
            : { ...current, [key]: id },
        );
      }}
    >
      <MenuItem value="">{emptyLabel}</MenuItem>
      {form[key] != null &&
        !records.some((record) => record.id === form[key]) && (
          <MenuItem disabled value={form[key]!}>
            {t("unavailableSelection")}
          </MenuItem>
        )}
      {records.map((record) => (
        <MenuItem key={record.id} value={record.id}>
          {record.name}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Typography variant="h4">{t("title")}</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            disabled={busy || loading}
            onClick={() => {
              setError("");
              void loadEvents();
            }}
          >
            {t("refreshEvents")}
          </Button>
          {canCreate && (
            <Button
              variant="contained"
              startIcon={<Add />}
              disabled={busy}
              onClick={() => openForm()}
            >
              {t("createEvent")}
            </Button>
          )}
        </Stack>
      </Stack>
      {error && !formOpen && !detail && !deleting && (
        <Alert severity="error" sx={{ mb: 2, whiteSpace: "pre-line" }}>
          {error}
        </Alert>
      )}
      {loading && <LoadingSpinner />}
      <Stack
        direction="row"
        spacing={2}
        sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}
      >
        <TextField
          select
          size="small"
          label={t("filterByType")}
          value={typeFilter}
          sx={{ minWidth: 180 }}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <MenuItem value="">{t("allTypes")}</MenuItem>
          {eventTypes.map((type) => (
            <MenuItem key={type} value={type}>
              {t(`types.${type}`)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          type="date"
          label={t("from")}
          slotProps={{ inputLabel: { shrink: true } }}
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          sx={{ minWidth: 150 }}
        />
        <TextField
          size="small"
          type="date"
          label={t("to")}
          slotProps={{ inputLabel: { shrink: true } }}
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          sx={{ minWidth: 150 }}
        />
        {(typeFilter || dateFrom || dateTo) && (
          <Button
            size="small"
            onClick={() => {
              setTypeFilter("");
              setDateFrom("");
              setDateTo("");
            }}
          >
            {t("clearFilters")}
          </Button>
        )}
      </Stack>
      <Paper sx={{ overflowX: "auto" }}>
        <Table aria-label={t("nurseryEventsLabel")}>
          <TableHead>
            <TableRow>
              {[
                t("date"),
                t("type"),
                t("titleColumn"),
                t("teacherColumn"),
                t("classroomChildColumn"),
                t("timeColumn"),
                t("mediaColumn"),
                t("autoAttachmentColumn"),
                t("actions"),
              ].map((label) => (
                <TableCell key={label}>{label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredEvents.map((event) => (
              <TableRow key={event.id}>
                <TableCell>{localDate(event.date)}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={t(`types.${event.type}`)}
                    sx={{ bgcolor: typeColors[event.type], fontWeight: 800 }}
                  />
                </TableCell>
                <TableCell>{event.title}</TableCell>
                <TableCell>{owner(event)}</TableCell>
                <TableCell>{target(event)}</TableCell>
                <TableCell>{time(event)}</TableCell>
                <TableCell>{event.media?.length || 0}</TableCell>
                <TableCell>{status(event)}</TableCell>
                <TableCell>
                  <IconButton
                    aria-label={t("manageMedia")}
                    disabled={busy}
                    onClick={() => {
                      setFiles([]);
                      void run((signal) => refreshDetail(event, signal));
                    }}
                  >
                    <PermMedia />
                  </IconButton>
                  {canEdit && (
                    <IconButton
                      aria-label={t("editEvent")}
                      disabled={busy}
                      onClick={() =>
                        void run(async (signal) => {
                          const result = await adminEventAPI.detail(
                            nurseryId,
                            event.id,
                            signal,
                          );
                          if (!signal.aborted)
                            openForm(checkEvent(result.event));
                        })
                      }
                    >
                      <Edit />
                    </IconButton>
                  )}
                  {canDelete && (
                    <IconButton
                      aria-label={t("deleteConfirm")}
                      disabled={busy}
                      onClick={() => {
                        setError("");
                        setDeleting(event);
                      }}
                    >
                      <Delete />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!loading && !events.length && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  {t("noEvents")}
                </TableCell>
              </TableRow>
            )}
            {!loading && events.length > 0 && !filteredEvents.length && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  {t("noEventsMatchFilters")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
      {busy && !formOpen && !detail && !deleting && <LoadingSpinner />}

      <Dialog
        open={formOpen}
        onClose={() => {
          if (!busy) setFormOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <Box component="form" onSubmit={submit} noValidate>
          <DialogTitle>{editing ? t("editEvent") : t("createEvent")}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              {error && <Alert severity="error" sx={{ whiteSpace: "pre-line" }}>{error}</Alert>}
              {captureChanged && <Alert severity="warning">{t("scheduleChangeConfirm")}</Alert>}
              {submitAttempted && validationError && (
                <Typography color="error" variant="body2">
                  {validationError}
                </Typography>
              )}
              {optionsLoading && <LoadingSpinner />}
              {!optionsReady && !optionsLoading && (
                <Button onClick={() => void loadOptions()}>
                  {t("retryLoadingOptions")}
                </Button>
              )}
              <TextField
                required
                fullWidth
                label={t("eventTitle")}
                value={form.title}
                disabled={busy}
                inputProps={{ maxLength: 255 }}
                onChange={(event) =>
                  setForm({ ...form, title: event.target.value })
                }
              />
              <TextField
                select
                label={t("type")}
                value={form.type}
                disabled={busy}
                onChange={(event) =>
                  setForm({
                    ...form,
                    type: event.target.value as AdminEventRequest["type"],
                  })
                }
              >
                {eventTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {t(`types.${type}`)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                required
                type="date"
                label={t("date")}
                slotProps={{ inputLabel: { shrink: true } }}
                value={form.date}
                disabled={busy}
                onChange={(event) =>
                  setForm({ ...form, date: event.target.value })
                }
              />
              {select(
                "teacher_id",
                t("teacherRequired"),
                options.teachers,
                t("selectTeacher"),
              )}
              {select(
                "classroom_id",
                t("classroom"),
                options.classrooms,
                t("allClassrooms"),
              )}
              {select(
                "child_id",
                t("child"),
                classroomOptions(options.children, form.classroom_id),
                t("allChildren"),
              )}
              {select(
                "camera_id",
                t("cameraOptional"),
                classroomOptions(options.cameras, form.classroom_id),
                t("noSpecificCamera"),
              )}
              <Stack direction="row" spacing={2}>
                <TextField
                  type="time"
                  label={t("startTime")}
                  slotProps={{ inputLabel: { shrink: true } }}
                  value={form.start_time || ""}
                  disabled={busy}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      start_time: event.target.value || null,
                      end_time: event.target.value ? form.end_time : null,
                    })
                  }
                />
                <TextField
                  type="time"
                  label={t("endTime")}
                  slotProps={{ inputLabel: { shrink: true } }}
                  value={form.end_time || ""}
                  disabled={busy || !form.start_time}
                  onChange={(event) =>
                    setForm({ ...form, end_time: event.target.value || null })
                  }
                />
              </Stack>
              <Typography variant="caption">
                {t("dateTimeHelper")}
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button disabled={busy} onClick={() => setFormOpen(false)}>
              {t("cancel")}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={
                busy || !optionsReady || (editing ? !canEdit : !canCreate)
              }
            >
              {busy ? t("saving") : t("save")}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        open={!!detail}
        onClose={() => {
          if (!busy) {
            setDetail(null);
            setFiles([]);
            setUploadingFiles({});
          }
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{detail?.title}</DialogTitle>
        <DialogContent>
          {detail && (
            <Stack spacing={2}>
              {error && removingMedia === null && <Alert severity="error" sx={{ whiteSpace: "pre-line" }}>{error}</Alert>}
              <Typography>
                {t(`types.${detail.type}`)} · {owner(detail)} · {target(detail)}
              </Typography>
              <Typography>
                {localDate(detail.date)} · {time(detail)}
                {detail.camera_id
                  ? ` · ${detail.camera?.name || `Camera #${detail.camera_id}`}`
                  : ""}
              </Typography>
              <Box>{status(detail)}</Box>
              {detail.auto_attach_error && (
                <Alert severity="error">{detail.auto_attach_error}</Alert>
              )}
              <Button
                disabled={busy}
                onClick={() =>
                  void run((signal) => refreshDetail(detail, signal))
                }
              >
                {t("refreshDetails")}
              </Button>
              {busy && <LoadingSpinner />}
              {!detail.media?.length && (
                <Typography>
                  {t("noMedia")}
                </Typography>
              )}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 2,
                }}
              >
                {(detail.media || []).map((media) => (
                  <Paper key={media.id} variant="outlined" sx={{ p: 1 }}>
                    {media.url || media.file_path ? (
                      media.media_type === "video" ? (
                        <video
                          controls
                          preload="metadata"
                          src={
                            media.url ??
                            `${import.meta.env.VITE_API_URL}/${media.file_path}`
                          }
                          style={{ width: "100%", maxHeight: 280 }}
                        />
                      ) : (
                        <img
                          src={
                            media.url ??
                            `${import.meta.env.VITE_API_URL}/${media.file_path}`
                          }
                          alt={media.original_filename || t("eventImage")}
                          style={{
                            width: "100%",
                            maxHeight: 280,
                            objectFit: "contain",
                          }}
                        />
                      )
                    ) : (
                      <Alert severity="info">
                        {t("previewUnavailable")}
                      </Alert>
                    )}
                    <Typography
                      variant="body2"
                      sx={{ overflowWrap: "anywhere" }}
                    >
                      {media.original_filename}
                    </Typography>
                    <Chip
                      size="small"
                      label={
                        media.source === "auto"
                          ? t("automatic")
                          : t("manual")
                      }
                    />
                    {canEdit && (
                      <Button
                        color="error"
                        disabled={busy}
                        onClick={() => { setError(""); setRemovingMedia(media.id); }}
                      >
                        {t("deleteMedia")}
                      </Button>
                    )}
                  </Paper>
                ))}
              </Box>
              {canEdit && (
                <>
                  <Typography variant="body2">
                    {t("uploadHelper")}
                  </Typography>
                  <Button component="label" variant="outlined" disabled={busy}>
                    {t("selectFiles")}
                    <input
                      key={fileInputKey}
                      hidden
                      aria-label="Event media files"
                      type="file"
                      accept={mediaAccept}
                      multiple
                      disabled={busy}
                      onChange={(event) => {
                        const selected = Array.from(event.target.files || []);
                        const invalid = validateMedia(selected);
                        setError(invalid || "");
                        setFiles(invalid ? [] : selected);
                        if (invalid) event.target.value = "";
                      }}
                    />
                  </Button>
                  <Box sx={{ mt: 2 }}>
                    {files.map((file, index) => (
                      <Typography key={index} variant="caption">
                        {file.name}: {uploadingFiles[file.name] || t("ready")}
                      </Typography>
                    ))}
                  </Box>
                  <Button
                    variant="contained"
                    disabled={busy || !files.length}
                    onClick={() =>
                      void run(async (signal) => {
                        if (!canEdit) return;
                        setUploadingFiles(
                          Object.fromEntries(files.map((f) => [f.name, "0%"])),
                        );
                        await adminEventAPI.upload(
                          nurseryId,
                          checkEvent(detail).id,
                          files,
                          signal,
                          (progress) => {
                            const total =
                              progress.total ||
                              files.reduce((sum, f) => sum + f.size, 0);
                            const percent = total
                              ? Math.min(
                                  100,
                                  Math.round((progress.loaded / total) * 100),
                                )
                              : 0;
                            setUploadingFiles((current) =>
                              Object.fromEntries(
                                Object.keys(current).map((name) => [
                                  name,
                                  `${percent}%`,
                                ]),
                              ),
                            );
                          },
                        );
                        if (signal.aborted) return;
                        setFiles([]);
                        setUploadingFiles({});
                        setFileInputKey((key) => key + 1);
                        await refreshDetail(detail, signal);
                      })
                    }
                  >
                    {busy ? t("uploading") : t("uploadSelectedFiles")}
                  </Button>
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={busy}
            onClick={() => {
              setDetail(null);
            setFiles([]);
            setUploadingFiles({});
          }}
        >
          {t("close")}
        </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!deleting || removingMedia !== null}
        onClose={() => {
          if (!busy) {
            setDeleting(null);
            setRemovingMedia(null);
          }
        }}
      >
        <DialogTitle>
          {deleting ? t("deleteEventConfirm") : t("removeMediaConfirm")}
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2, whiteSpace: "pre-line" }}>{error}</Alert>}
          {!deleting && detail?.media.find((media) => media.id === removingMedia)?.source === "auto" && (
            <Alert severity="warning" sx={{ mb: 2 }}>{t("removeAutoMediaWarning")}</Alert>
          )}
          <Typography>
            {deleting
              ? t("deleteEventBody", { title: deleting.title })
              : t("removeMediaBody")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={busy}
            onClick={() => {
              setDeleting(null);
              setRemovingMedia(null);
            }}
          >
            {t("cancel")}
          </Button>
          <Button
            color="error"
            disabled={busy || (deleting ? !canDelete : !canEdit)}
            onClick={() =>
              void run(async (signal) => {
                if (deleting && canDelete) {
                  await adminEventAPI.remove(
                    nurseryId,
                    checkEvent(deleting).id,
                    signal,
                  );
                  if (signal.aborted) return;
                  setDeleting(null);
                  await loadEvents();
                } else if (detail && removingMedia !== null && canEdit) {
                  await adminEventAPI.removeMedia(
                    nurseryId,
                    checkEvent(detail).id,
                    removingMedia,
                    signal,
                  );
                  if (signal.aborted) return;
                  setRemovingMedia(null);
                  setDetail((current) => current ? {
                    ...current,
                    media: current.media.filter((media) => media.id !== removingMedia),
                  } : current);
                  await refreshDetail(detail, signal);
                }
              })
            }
          >
            {busy ? t("removing") : t("confirmRemoval")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
